import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import Pagination from "../components/Pagination";

function ListaContactosGenerales() {
  const [contactosGenerales, setContactosGenerales] = useState([]);
  const [todosContactos, setTodosContactos] = useState([]); // Lista de todos los contactos para el dropdown
  const [searchTerm, setSearchTerm] = useState("");
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);

  // Estados para el formulario
  const [selectedContactoId, setSelectedContactoId] = useState("");
  const [prioridad, setPrioridad] = useState("contacto_por_defecto");
  const [editando, setEditando] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Obtener contactos generales con paginación
  const obtenerContactosGenerales = useCallback(
    (url = `${API_URL}/api/contactos-generales/?page_size=100`) => {
      axios.get(url)
        .then(response => {
          if (response.data.results) {
            setContactosGenerales(response.data.results);
            setNextPage(response.data.next);
            setPrevPage(response.data.previous);
          } else {
            setContactosGenerales(response.data);
          }
        })
        .catch(error => {
          console.error("Error al obtener contactos generales:", error);
          setContactosGenerales([]);
        });
    },
    [API_URL]
  );

  // Obtener todos los contactos para el dropdown
  const obtenerTodosContactos = useCallback(() => {
    axios.get(`${API_URL}/api/contactos/?page_size=1000`)
      .then(response => {
        let contactos = response.data.results || response.data;
  
        // 🧠 Ordenar alfabéticamente por `persona`
        contactos.sort((a, b) => a.persona.localeCompare(b.persona, 'es', { sensitivity: 'base' }));
  
        setTodosContactos(contactos);
      })
      .catch(error => {
        console.error("Error al obtener todos los contactos:", error);
        setTodosContactos([]);
      });
  }, [API_URL]);

  useEffect(() => {
    obtenerContactosGenerales();
    obtenerTodosContactos();
  }, [obtenerContactosGenerales, obtenerTodosContactos]);

  // Buscar contactos generales
  const buscarContactos = () => {
    obtenerContactosGenerales(`${API_URL}/api/contactos-generales/?search=${searchTerm}&page_size=5`);
  };

  // Función para manejar el envío del formulario
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedContactoId) {
      alert("Por favor, selecciona un contacto.");
      return;
    }

    const data = {
      contacto: parseInt(selectedContactoId),
      prioridad
    };

    // Verificar si ya existe un contacto general con la misma prioridad
    const existente = contactosGenerales.find(cg => cg.prioridad === prioridad);

    if (existente && !editando) {
      if (window.confirm("Ya existe un contacto asignado a esta prioridad. ¿Deseas reemplazarlo?")) {
        // Reemplazar (actualizar) el contacto general existente
        axios.put(`${API_URL}/api/contactos-generales/${existente.id}/`, data, {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": Cookies.get("csrftoken")
          },
          withCredentials: true,
        })
        .then(response => {
          setContactosGenerales(contactosGenerales.map(cg => cg.id === response.data.id ? response.data : cg));
          setSelectedContactoId("");
          setPrioridad("contacto_por_defecto");
        })
        .catch(error => {
          console.error("Error al actualizar contacto general:", error);
          alert("Error al actualizar el contacto general.");
        });
      } else {
        return;
      }
    } else {
      if (editando) {
        // Actualización del contacto general (modo edición)
        axios.put(`${API_URL}/api/contactos-generales/${editando.id}/`, data, {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": Cookies.get("csrftoken")
          },
          withCredentials: true,
        })
        .then(response => {
          setContactosGenerales(contactosGenerales.map(cg => cg.id === response.data.id ? response.data : cg));
          setEditando(null);
          setSelectedContactoId("");
          setPrioridad("contacto_por_defecto");
        })
        .catch(error => {
          console.error("Error al actualizar contacto general:", error);
          alert("Error al actualizar el contacto general.");
        });
      } else {
        // Creación de un nuevo contacto general
        axios.post(`${API_URL}/api/contactos-generales/`, data, {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": Cookies.get("csrftoken")
          },
          withCredentials: true,
        })
        .then(response => {
          setContactosGenerales([...contactosGenerales, response.data]);
          setSelectedContactoId("");
          setPrioridad("contacto_por_defecto");
        })
        .catch(error => {
          console.error("Error al crear contacto general:", error.response.data);
          alert(error.response?.data?.error || "Error al crear el contacto general.");
        });
      }
    }
  };

  const iniciarEdicion = (contacto) => {
    setEditando(contacto);
    setSelectedContactoId(contacto.contacto); // Suponiendo que 'contacto' es el ID del contacto
    setPrioridad(contacto.prioridad);
  };

  const cancelarEdicion = () => {
    setEditando(null);
    setSelectedContactoId("");
    setPrioridad("contacto_por_defecto");
  };

  // Función para eliminar un contacto general
  const eliminarContacto = (id) => {
    if (window.confirm("¿Estás seguro de eliminar este contacto general?")) {
      axios.delete(`${API_URL}/api/contactos-generales/${id}/`, {
        headers: {
          "X-CSRFToken": Cookies.get("csrftoken")
        },
        withCredentials: true,
      })
      .then(() => {
        setContactosGenerales(contactosGenerales.filter(cg => cg.id !== id));
      })
      .catch(error => {
        console.error("Error al eliminar contacto general:", error);
        alert("Error al eliminar el contacto general.");
      });
    }
  };

  return (
    <div>
      <h3>Gestión de Contactos Generales</h3>
      {/* Formulario de creación/edición y búsqueda */}
      <div className="card p-3 mt-3">
        <form onSubmit={handleSubmit} className="d-flex align-items-center gap-2 flex-wrap">
          <select
            value={selectedContactoId}
            onChange={(e) => setSelectedContactoId(e.target.value)}
            className="form-control"
            style={{ width: "150px" }}
            required
          >
            <option value="">Selecciona un contacto</option>
            {todosContactos.map((contacto) => (
              <option key={contacto.id} value={contacto.id}>
                {contacto.persona} - {contacto.telefono}
              </option>
            ))}
          </select>
          <select
            value={prioridad}
            onChange={(e) => setPrioridad(e.target.value)}
            className="form-control"
            style={{ width: "150px" }}
            required
          >
            <option value="contacto_por_defecto">Contacto por defecto</option>
            <option value="segunda_opcion">2ª opción</option>
            <option value="tercera_opcion">3ª opción</option>
            <option value="cuarta_opcion">4ª opción</option>
          </select>
          <button type="submit" className="btn btn-primary">
            {editando ? "Actualizar" : "Agregar"}
          </button>
          {editando && (
            <button type="button" className="btn btn-secondary" onClick={cancelarEdicion}>
              Cancelar
            </button>
          )}
        </form>
        <div className="d-flex align-items-center gap-2 mt-3 flex-wrap">
          <input
            type="text"
            className="form-control"
            placeholder="Buscar..."
            value={searchTerm}
            maxLength={15}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "150px" }}
          />
          <button className="btn btn-primary" onClick={buscarContactos}>
            Buscar
          </button>
        </div>
      </div>

      {/* Tabla de contactos generales */}
      <div className="card p-3 mt-3">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Prioridad</th>
              <th>Contacto</th>
              <th>Teléfono</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {contactosGenerales && contactosGenerales.length > 0 ? (
              contactosGenerales.map((contacto) => (
                <tr key={contacto.id}>
                  <td>{contacto.prioridad.replace("_", " ")}</td>
                  <td>{contacto.contacto_nombre}</td>
                  <td>{contacto.contacto_telefono}</td>
                  <td>
                    <button className="btn btn-warning btn-sm" onClick={() => iniciarEdicion(contacto)}>
                      Editar
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => eliminarContacto(contacto.id)}>
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4">No hay contactos generales disponibles.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="d-flex justify-content-between mt-3">
        <button className="btn btn-secondary" disabled={!prevPage} onClick={() => obtenerContactosGenerales(prevPage)}>
          Anterior
        </button>
        <button className="btn btn-secondary" disabled={!nextPage} onClick={() => obtenerContactosGenerales(nextPage)}>
          Siguiente
        </button>
      </div>
    </div>
  );
}

export default ListaContactosGenerales;
